import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  List,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useCallback, useMemo } from "react";

import { usePaymasters } from "../../../api/hooks/paymaster/usePaymasters.ts";
import ActionIcon from "../../../components/ActionIcon/ActionIcon.tsx";
import SvgDelete from "../../../components/Icons/Delete.tsx";
import SvgPlus from "../../../components/Icons/Plus.tsx";
import { Select } from "../../../components/Select.tsx";
import {
  LimitStatus,
  UpdatePaymasterInput,
} from "../../../graphql/generated.ts";
import { CustomerOption } from "../../../types/customer.ts";
import { LimitDetail } from "../../../types/limit.ts";
import { Paymaster } from "../../../types/paymaster.ts";
import { getCompanyOptions } from "../../../utils/company.ts";
import { toHumanReadable } from "../../../utils/string.ts";
import { useStyles } from "./CreatePaymasterModal.styles.tsx";

interface Props {
  data: Paymaster | null | undefined;
  customers: CustomerOption[] | null | undefined;
  limitDetails: LimitDetail[] | null | undefined;
  opened?: boolean;
  onClose: () => void;
  onSubmit: (value: UpdatePaymasterInput) => void;
}

export const CreatePaymasterModal = ({
  data,
  customers,
  limitDetails,
  opened = false,
  onClose,
  onSubmit,
}: Props) => {
  const { classes } = useStyles({ variant: "golden" });

  const [{ data: paymasterData }] = usePaymasters({});

  const paymasters = paymasterData?.paymasters?.data;

  const normalizeString = useCallback((str: string) => {
    return str.toLowerCase().trim();
  }, []);

  const checkExactNameMatch = useCallback(
    (name: string) => {
      if (!paymasters || !name.trim()) return false;

      const normalizedInput = normalizeString(name);
      return paymasters.some(
        (paymaster) =>
          paymaster.id !== data?.id &&
          normalizeString(paymaster.name) === normalizedInput
      );
    },
    [paymasters, data?.id, normalizeString]
  );

  const getSimilarPaymasters = useCallback(
    (name: string) => {
      if (!paymasters || !name.trim() || name.trim().length < 2) return [];

      const normalizedInput = normalizeString(name);

      return paymasters
        .filter(
          (paymaster) =>
            paymaster.id !== data?.id &&
            normalizeString(paymaster.name).includes(normalizedInput)
        )
        .slice(0, 5)
        .map((paymaster) => paymaster.name);
    },
    [paymasters, data?.id, normalizeString]
  );

  const form = useForm({
    initialValues: {
      name: data?.name ?? "",
      customers: data?.customerPaymasters.length
        ? data.customerPaymasters.map((c) => ({
            id: c.id,
            customerId: String(c.customer.id),
            limitDetailId: c.limitDetail && String(c.limitDetail.id),
          }))
        : [
            {
              id: undefined,
              customerId: undefined,
              limitDetailId: undefined,
            },
          ],
    },
    validate: {
      name: (value) => {
        if (!value || !value.trim()) {
          return "Paymaster Name is required";
        }
        if (checkExactNameMatch(value)) {
          return "A paymaster with this name already exists";
        }
        return null;
      },
      customers: {
        customerId: isNotEmpty("Customer is required"),
        limitDetailId: isNotEmpty("Limit is required"),
      },
    },
    validateInputOnBlur: true,
    clearInputErrorOnChange: true,
  });

  const nameValidation = useMemo(() => {
    const currentName = form.values.name;
    const hasExactMatch = checkExactNameMatch(currentName);
    const similarNames = getSimilarPaymasters(currentName);

    return {
      hasExactMatch,
      similarNames: similarNames.filter(
        (name) => normalizeString(name) !== normalizeString(currentName)
      ),
    };
  }, [
    checkExactNameMatch,
    getSimilarPaymasters,
    normalizeString,
    form.values.name,
  ]);

  const getLimitDetailsOptions = useCallback(
    (customerId: string | undefined, isDisabled: boolean) => {
      if (!limitDetails || !customerId) {
        return [];
      }
      if (customerId && isDisabled) {
        const customerPaymaster = data?.customerPaymasters.find(
          (customer) => customer.customer.id === Number(customerId)
        );
        if (customerPaymaster?.limitDetail) {
          return [
            {
              label: `${
                customerPaymaster.limitDetail.limitName
              } (${toHumanReadable(customerPaymaster.limitDetail.status)})`,
              value: String(customerPaymaster.limitDetail.id),
            },
          ];
        }
      }
      return limitDetails
        .filter((limitDetail) => limitDetail.customerId === Number(customerId))
        .filter((limitDetail) => limitDetail.status === LimitStatus.Active)
        .map((limitDetail) => ({
          label: limitDetail.limitName ?? "",
          value: String(limitDetail.id),
        }));
    },
    [data?.customerPaymasters, limitDetails]
  );

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid() || nameValidation.hasExactMatch) {
      return;
    }

    onSubmit({
      ...form.values,
      customers: form.values.customers.map((customer) => ({
        id: customer.id ?? null,
        customerId: Number(customer.customerId),
        limitDetailId: Number(customer.limitDetailId),
      })),
    });
  }, [form, onSubmit, nameValidation.hasExactMatch]);

  const handleAddCustomer = useCallback(() => {
    form.insertListItem("customers", {
      id: undefined,
      customerId: undefined,
      limitDetailId: undefined,
    });
  }, [form]);

  const handleDeleteCustomer = useCallback(
    (index: number) => {
      form.removeListItem("customers", index);
    },
    [form]
  );

  const renderCustomerForm = useCallback(
    (_: (typeof form.values.customers)[0], index: number) => {
      const limitDetailIsDisabled =
        !form.values.customers[index].customerId ||
        (!!form.values.customers[index].id &&
          !!form.values.customers[index].limitDetailId);
      return (
        <Card key={index} className={classes.wrapper}>
          {!data?.id && (
            <Flex justify="end">
              <ActionIcon
                variant="error"
                withBorder={false}
                onClick={() => handleDeleteCustomer(index)}
              >
                <SvgDelete />
              </ActionIcon>
            </Flex>
          )}
          <Flex direction="column" gap={12}>
            <Select
              required
              size="m"
              searchable
              withinPortal={true}
              disabled={!!form.values.customers[index].id}
              label="Customer"
              placeholder="Select Customer"
              data={getCompanyOptions(customers)}
              {...form.getInputProps(`customers.${index}.customerId`)}
            />
            <Select
              required
              size="m"
              searchable
              withinPortal={true}
              label="Limit"
              placeholder="Select Limit"
              disabled={limitDetailIsDisabled}
              data={getLimitDetailsOptions(
                form.values.customers[index].customerId,
                limitDetailIsDisabled
              )}
              {...form.getInputProps(`customers.${index}.limitDetailId`)}
            />
          </Flex>
        </Card>
      );
    },
    [
      classes.wrapper,
      customers,
      data?.id,
      form,
      getLimitDetailsOptions,
      handleDeleteCustomer,
    ]
  );

  const showValidationAlerts =
    !data?.id || (data?.id && form.values.name !== data?.name);

  return (
    <>
      <Modal
        opened={opened}
        title={data?.id ? "Update Paymaster" : "Add Paymaster"}
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        size="xl"
        scrollAreaComponent={ScrollArea.Autosize}
        onClose={handleClose}
      >
        <Flex direction="column" gap={20}>
          <Stack>
            <Box>
              <TextInput
                required
                size="m"
                w="50%"
                label="Paymaster Name"
                placeholder="Enter Paymaster Name"
                {...form.getInputProps("name")}
              />

              {/* Show exact match warning */}
              {showValidationAlerts && nameValidation.hasExactMatch && (
                <Alert color="red" mt="xs">
                  <Text size="sm" weight={500}>
                    A paymaster with the name {form.values.name} already exists.
                  </Text>
                  <Text size="sm" mt={4}>
                    Please edit the existing paymaster or choose a different
                    name.
                  </Text>
                </Alert>
              )}

              {/* Show similar names suggestions */}
              {showValidationAlerts &&
                !nameValidation.hasExactMatch &&
                nameValidation.similarNames.length > 0 && (
                  <Alert color="yellow" mt="xs">
                    <Text size="sm" weight={500}>
                      Similar paymaster names found:
                    </Text>
                    <List size="sm" mt={4}>
                      {nameValidation.similarNames.map((name, index) => (
                        <List.Item key={index}>{name}</List.Item>
                      ))}
                    </List>
                    <Text size="sm" mt={4}>
                      Make sure you&apos;re not creating a duplicate. Consider
                      editing an existing paymaster instead.
                    </Text>
                  </Alert>
                )}
            </Box>

            <Card padding={0} radius={4} pb={10}>
              <Flex justify="space-between" pb={10} pt={10}>
                <Flex gap={12} align="center">
                  <Box className={classes.pin} />
                  <Text className={classes.label}>Customers</Text>
                </Flex>
                <Button
                  uppercase
                  variant="filledGolden"
                  leftIcon={<SvgPlus />}
                  size="xs"
                  disabled={!form.isValid() || nameValidation.hasExactMatch}
                  onClick={handleAddCustomer}
                >
                  Add
                </Button>
              </Flex>
              <Divider pb={10} />
              <SimpleGrid
                cols={2}
                breakpoints={[{ maxWidth: "lg", cols: 1 }]}
                spacing={10}
                verticalSpacing={12}
              >
                {form.values.customers.map(renderCustomerForm)}
              </SimpleGrid>
            </Card>
          </Stack>
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={onClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid() || nameValidation.hasExactMatch}
              onClick={handleSubmit}
            >
              {data?.id ? "update" : "add"}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
